"use client";

import { cn } from "@repo/design-system/lib/utils";
import {
	type ComponentProps,
	createContext,
	isValidElement,
	type ReactNode,
	useContext,
} from "react";
import {
	Tooltip as AriaTooltip,
	TooltipTrigger as AriaTooltipTrigger,
	Focusable,
} from "react-aria-components";

/**
 * Radix-free Tooltip — keeps the shadcn compound API (TooltipProvider / Tooltip
 * / TooltipTrigger / TooltipContent) on react-aria. `TooltipTrigger asChild`
 * wraps its child in react-aria `Focusable` so any element becomes a valid
 * hover/focus trigger. Used by the sidebar (collapsed item labels).
 */

type AriaPlacement = NonNullable<
	ComponentProps<typeof AriaTooltip>["placement"]
>;

const TooltipDelayContext = createContext<number | undefined>(undefined);

function TooltipProvider({
	children,
	delayDuration,
}: {
	children?: ReactNode;
	delayDuration?: number;
}) {
	return (
		<TooltipDelayContext.Provider value={delayDuration}>
			{children}
		</TooltipDelayContext.Provider>
	);
}

interface TooltipProps {
	children?: ReactNode;
	open?: boolean;
	defaultOpen?: boolean;
	onOpenChange?: (open: boolean) => void;
	delayDuration?: number;
}

function Tooltip({
	children,
	open,
	defaultOpen,
	onOpenChange,
	delayDuration,
}: TooltipProps) {
	const ctxDelay = useContext(TooltipDelayContext);
	const delay = delayDuration ?? ctxDelay ?? 300;
	return (
		<AriaTooltipTrigger
			delay={delay}
			closeDelay={0}
			isOpen={open}
			defaultOpen={defaultOpen}
			onOpenChange={onOpenChange}
		>
			{children}
		</AriaTooltipTrigger>
	);
}

function TooltipTrigger({
	asChild,
	children,
	className,
}: {
	asChild?: boolean;
	children?: ReactNode;
	className?: string;
}) {
	if (asChild && isValidElement(children)) {
		return (
			<Focusable>
				{children as ComponentProps<typeof Focusable>["children"]}
			</Focusable>
		);
	}
	return (
		<Focusable>
			<button type="button" className={className}>
				{children}
			</button>
		</Focusable>
	);
}

interface TooltipContentProps {
	className?: string;
	side?: "top" | "right" | "bottom" | "left";
	align?: "start" | "center" | "end";
	sideOffset?: number;
	hidden?: boolean;
	children?: ReactNode;
}

function TooltipContent({
	className,
	side = "top",
	align = "center",
	sideOffset = 4,
	hidden,
	children,
}: TooltipContentProps) {
	if (hidden) {
		return null;
	}
	const placement = (
		align === "center" ? side : `${side} ${align}`
	) as AriaPlacement;
	return (
		<AriaTooltip
			offset={sideOffset}
			placement={placement}
			className={(state) =>
				cn(
					"z-50 overflow-hidden rounded-lg bg-primary-solid px-3 py-2 text-white text-xs shadow-lg",
					state.isEntering && "fade-in-0 zoom-in-95 animate-in",
					state.isExiting && "fade-out-0 zoom-out-95 animate-out",
					className,
				)
			}
		>
			<span className="dark contents">{children}</span>
		</AriaTooltip>
	);
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
